<template lang="pug">
  validation-observer(
    ref="obs"
    slim
    v-slot="obs"
  )
    form(@submit.prevent="submit")
      slot(
        :fields="processedFields"
        :validate="validate"
        :form-data="formData"
        :submit="submit"
        :reset="reset"
        :info="info"
      )
</template>

<script>
import get from 'lodash/get';
import set from 'lodash/set';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import template from 'lodash/template';
import templateSettings from 'lodash/templateSettings';

export default {
  props: {
    submitCallback: {
      type: Function,
      default: () => null,
    },
    schema: {
      type: Array,
      required: true,
    },
    namespace: {
      type: String,
      default: '',
    },
    nestable: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  inject: {
    getParentFormData: {
      from: 'getFormData',
      default: null,
    },
    getParentBufferData: {
      from: 'getBufferData',
      default: null,
    },
    setParentFormData: {
      from: 'setFormData',
      default: null,
    },
  },
  data() {
    return {
      initialData: { ...this.value },
      bufferData: { ...this.value },
    };
  },
  computed: {
    hasParentForm() {
      return this.getParentFormData !== null && this.nestable;
    },
    formData: {
      get() {
        return this.hasParentForm ? this.getParentFormData() : this.bufferData;
      },
      set(val) {
        this.bufferData = val;
      },
    },
    info() {
      const { schema, formData } = this;

      return schema
        .map((item) => {
          if (!item.infoTemplate) return '';

          const value = get(formData, item.value);

          if (isNil(value)) return '';

          templateSettings.interpolate = /{{([\s\S]+?)}}/g;
          const compiled = template(item.infoTemplate);

          return compiled({ value, item });
        })
        .filter(Boolean);
    },
    processedFields() {
      const { namespace } = this;

      return this.schema.reduce((acc, field) => {
        const fieldName = field.name;

        if (!fieldName) return acc;

        const value = [namespace, field.value].filter(Boolean).join('.');

        acc[fieldName] = {
          ...field,
          value,
        };
        return acc;
      }, {});
    },
  },
  watch: {
    formData: {
      handler(val) {
        this.$emit('input', val);
      },
      deep: true,
    },
    bufferData: {
      handler(val) {
        const { formData } = this;

        if (isEqual(formData, val)) return;

        const { namespace } = this;
        const newFormData = this.schema.reduce((acc, field) => {
          const fullPath = [namespace, field.value].filter(Boolean).join('.');
          const initial = get(val, fullPath);

          if (namespace && isEmpty(initial)) {
            set(acc, namespace);
            return acc;
          }

          if (has(formData, fullPath)) {
            set(acc, fullPath, initial);
          }

          return acc;
        }, this.getParentBufferData());

        this.setParentFormData(newFormData);
      },
    },
  },
  provide() {
    return {
      getFormData: () => this.formData,
      setFormData: (val) => { this.formData = val; },
      getBufferData: () => this.bufferData,
    };
  },
  methods: {
    submit() {
      const { reset, submitCallback } = this;
      this.$refs.obs.handleSubmit(
        () => submitCallback({ reset }),
      );
    },
    reset(initial = this.initialData) {
      this.formData = { ...initial };
      this.$refs?.obs?.reset?.();
    },
    validate(props = { silent: false }) {
      return this.$refs?.obs?.validate?.(props);
    },
  },
};
</script>
